'use client'

import {AutoComplete as NextAutoComplete, AutoCompleteProps as KitAutoCompleteProps} from 'antd'
import * as React from 'react'
import {useEffect, useMemo, useState} from 'react'
import debounce from 'lodash/debounce'
import {DefaultOptionType} from 'antd/lib/select'
import type {BaseSelectRef} from 'rc-select'
import type {BaseOptionType} from 'antd/es/select'

type AutocompleteOption = DefaultOptionType & {
  key: string | number | null | undefined
  label: React.ReactNode
  _normalized: string
  _parts: string[]
}

function normalizeOptions(options: DefaultOptionType[]): AutocompleteOption[] {
  return options.map(option => {
    const normalized = typeof option.label === 'string' ? normalize(option.label) : ''

    return {
      ...option,
      key: option.key,
      label: option.label,
      _normalized: normalized,
      _parts: normalized.split(','), // если нужно сравнение по частям
    }
  })
}

function normalize(str: string) {
  return str.toLowerCase().trim()
}

export interface AutoCompleteProps<
  ValueType = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
>
  extends
    React.PropsWithChildren<KitAutoCompleteProps<ValueType, OptionType>>,
    React.RefAttributes<BaseSelectRef> {
  onEmpty?: () => void
}

function parseSearch(inputString: string) {
  return inputString
    .split(/[,]/) // Разделяем по запятым
    .map(part => normalize(part)) // Убираем лишние пробелы
    .filter(part => part.length > 0) // Убираем пустые части
    .flatMap(part => part.split(/\s+/)) // Делим по пробелам внутри каждой части
    .filter(part => !/^\d+$/.test(part) || part)
}

function filterOptions(search: string, options: AutocompleteOption[]) {
  const searchParts = parseSearch(search)

  return options.filter(option => {
    const optionParts = option._parts

    for (let i = 0; i < searchParts.length; i++) {
      let found = false

      for (let j = 0; j < optionParts.length; j++) {
        if (optionParts[j]!.includes(searchParts[i]!)) {
          found = true
          break
        }
      }

      if (!found) return false
    }

    return true
  })
}

const useAutoCompleteSearch = (
  set: (options: AutocompleteOption[]) => void,
  options: AutocompleteOption[],
) => {
  return debounce(value => {
    set(filterOptions(value, options))
  }, 1000)
}

export const AutoComplete = ({onEmpty, ...props}: AutoCompleteProps) => {
  const {allowClear = true, filterOption = false} = props

  const options = useMemo(() => normalizeOptions(props.options ?? []), [props.options])

  const [filteredOptions, setFilteredOptions] = useState(options)

  const filteredValue = useMemo(
    () => (props.value ? options.find(o => o.value === props.value)?.label ?? props.value : props.value),
    [props.value, options],
  )

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    if (!filteredValue) {
      if (onEmpty) {
        onEmpty()
      }
    }
  }, [filteredValue])

  const defaultSearch = useAutoCompleteSearch(setFilteredOptions, options)

  const onSearch = props.onSearch ? props.onSearch : defaultSearch

  return (
    <NextAutoComplete
      {...props}
      value={filteredValue}
      filterOption={filterOption}
      options={filteredOptions}
      allowClear={allowClear}
      onSearch={onSearch}
      variant="filled"
      style={{width: '100%' }}
    />
  )
}
